import {
  type ApolloError,
  type OperationVariables,
  type PreloadQueryFetchPolicy,
  type QueryRef,
  skipToken,
  type TypedDocumentNode,
  useApolloClient,
  useBackgroundQuery,
  useReadQuery,
} from '@apollo/client';
import { canonicalStringify, getOperationName } from '@apollo/client/utilities';
import { useCallback, useEffect, useState } from 'react';
import { useRouteLoaderData } from './routeLoaderData';
import { isRouteQueryRefFor, retainRouteQueryRef } from './routePreload';

export type RouteQueryRefOptions = Readonly<{ fetchPolicy: PreloadQueryFetchPolicy }>;

type RouteQueryLatch<TData, TVariables> = Readonly<{
  variablesKey: string;
  source: 'loader' | 'page';
  loaderRef: QueryRef<TData, TVariables> | undefined;
}>;

function loaderRefFor<TData, TVariables extends OperationVariables>(
  loaderData: unknown,
  document: TypedDocumentNode<TData, TVariables>,
  variables: TVariables
): QueryRef<TData, TVariables> | undefined {
  if (typeof loaderData !== 'object' || loaderData === null) return undefined;
  for (const value of Object.values(loaderData)) {
    if (isRouteQueryRefFor(value, document, variables)) return value;
  }
  return undefined;
}

/** The ref this route's loader preloaded for exactly this document and these variables, if there is one. */
export function useRouteLoaderQueryRef<TData, TVariables extends OperationVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  variables: TVariables
): QueryRef<TData, TVariables> | undefined {
  return loaderRefFor(useRouteLoaderData(), document, variables);
}

/**
 * The page's ref for its first query: the one its route loader preloaded for this document and these
 * variables, or, with no such ref (cold mount before auth, router-less tests), one this hook starts.
 * The source is fixed per variables, so a later loader run never starts a second request; a loader
 * ref is replaced only by a newer loader ref (a retry). Read it below a `RouteQueryBoundary`.
 */
export function useRouteQueryRef<TData, TVariables extends OperationVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  variables: TVariables,
  { fetchPolicy }: RouteQueryRefOptions
): QueryRef<TData, TVariables> {
  const loaderRef = loaderRefFor(useRouteLoaderData(), document, variables);
  const variablesKey = canonicalStringify(variables);
  const [latch, setLatch] = useState<RouteQueryLatch<TData, TVariables>>(() => ({
    variablesKey,
    source: loaderRef === undefined ? 'page' : 'loader',
    loaderRef,
  }));

  let current = latch;
  if (latch.variablesKey !== variablesKey) {
    current = { variablesKey, source: loaderRef === undefined ? 'page' : 'loader', loaderRef };
  } else if (latch.source === 'loader' && loaderRef !== undefined && loaderRef !== latch.loaderRef) {
    current = { ...latch, loaderRef };
  }
  if (current !== latch) setLatch(current);

  const [pageRef] = useBackgroundQuery(document, current.source === 'page' ? { variables, fetchPolicy } : skipToken);
  const queryRef = current.source === 'loader' ? current.loaderRef : pageRef;
  if (queryRef === undefined) throw new Error(`useRouteQueryRef: no query ref for ${variablesKey}`);
  return queryRef;
}

export type RouteQueryResult<TData> = Readonly<{
  data: TData;
  /** Only set under errorPolicy 'all'; otherwise an error reaches the boundary instead. */
  error: ApolloError | undefined;
  /** Refetches in place: the page keeps its data until the response lands, like `useQuery`. */
  refetch: () => Promise<unknown>;
}>;

/**
 * Refetches the mounted query for this document and these variables in place. It goes through
 * `refetchQueries`, not `useQueryRefHandlers`, because a handler refetch suspends the reader again.
 */
export function useRouteQueryRefetch<TData, TVariables extends OperationVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  variables: TVariables
): () => Promise<unknown> {
  const client = useApolloClient();
  const operationName = getOperationName(document);
  const variablesKey = canonicalStringify(variables);
  return useCallback(
    () =>
      client.refetchQueries({
        include: operationName === null ? [document] : [operationName],
        onQueryUpdated: (observable) => canonicalStringify(observable.variables) === variablesKey,
      }),
    [client, document, operationName, variablesKey]
  );
}

/** Reads a route query ref below a `RouteQueryBoundary`: suspends until it has data; errors reach the boundary. */
export function useRouteQueryResult<TData, TVariables extends OperationVariables>(
  queryRef: QueryRef<TData, TVariables>,
  document: TypedDocumentNode<TData, TVariables>,
  variables: TVariables
): RouteQueryResult<TData> {
  const { data, error } = useReadQuery(queryRef);
  const refetch = useRouteQueryRefetch(document, variables);
  useEffect(() => retainRouteQueryRef(queryRef), [queryRef]);
  return { data, error, refetch };
}
